import type { Api, ProgramPhase } from "api-client";
import { sort } from "fast-sort";
import { makeAutoObservable } from "mobx";
import { assert, mapBy, orderUtilsMutable } from "utils";

import {
  type WorkoutCardAction,
  workoutCardActions,
} from "src/components/WorkoutCardMenuConfig";
import {
  type GridStrategy,
  type GroupedWorkouts,
  type ProgramGridMode,
  createGridStrategy,
} from "src/lib/grid/gridStrategies";

import { WorkoutGridStore } from "./WorkoutGridStore";

export type { ProgramGridMode } from "src/lib/grid/gridStrategies";

export type ViewContext =
  | "library"
  | "athleteLibrary"
  | "sharedProgram"
  | "athleteView"
  | "workflow";

export interface ProgramBuilderStoreInit {
  programGet: Api.GetProgram.Output.Self;
  viewContext: ViewContext;
  isCoach: boolean;
  gridMode?: ProgramGridMode;
  locale?: string;
  getSourceName?: () => string;
}

export const addButtonActions = {
  addWorkout: "addWorkout",
  assignProgram: "assignProgram",
} as const;

export type AddButtonAction =
  (typeof addButtonActions)[keyof typeof addButtonActions];

export type ProgramViewMode = "grid" | "periodization";

type ProgramBulkOperationResult =
  | Api.BulkCopyWorkout.Output.Self
  | Api.BulkDeleteWorkout.Output.Self;

export class ProgramBuilderStore {
  _program: Api.GetProgram.Output.Self;
  _viewContext: ViewContext;
  _gridStrategy: GridStrategy;
  _rowCount?: number;
  _viewMode: ProgramViewMode;
  _selectedCellIds: Map<number, boolean>;
  _getSourceName: () => string;
  workoutGrid: WorkoutGridStore;
  private readonly _isCoach: boolean;
  private readonly _locale: string;

  constructor(init: ProgramBuilderStoreInit) {
    this._program = init.programGet;
    this._viewContext = init.viewContext;
    this._isCoach = init.isCoach;
    this._locale = init.locale ?? "en";
    this._gridStrategy = createGridStrategy(this, this._locale, init.gridMode);
    this._rowCount = undefined;
    this._viewMode = "grid";
    this._selectedCellIds = new Map();
    this._getSourceName =
      init.getSourceName ??
      (() => {
        return this.program.name;
      });
    this.workoutGrid = new WorkoutGridStore({
      getWorkouts: () => {
        return this.workouts;
      },
      isWorkoutCardSelectable: (programWorkoutId) => {
        return (
          this.canEditWorkouts &&
          Boolean(this.programWorkoutsById[programWorkoutId])
        );
      },
      isWorkoutCardCopyable: () => {
        return true;
      },
      isWorkoutCardTask: () => {
        return false;
      },
      isWorkoutCardDeletable: () => {
        return this.canEditWorkouts;
      },
      isWorkoutEditable: () => {
        return this.canEditWorkouts;
      },
    });

    makeAutoObservable(this, { workoutGrid: false });
  }

  get gridMode(): ProgramGridMode {
    return this._gridStrategy.mode;
  }

  get isDated(): boolean {
    return this._gridStrategy.mode.type === "dated";
  }

  get isWorkflow(): boolean {
    return this._viewContext === "workflow";
  }

  get canEditWorkouts(): boolean {
    return this._viewContext !== "athleteView";
  }

  get viewMode(): ProgramViewMode {
    return this._viewMode;
  }

  setViewMode(mode: ProgramViewMode): void {
    this._viewMode = mode;
  }

  get program() {
    assert(this._program !== null, "Program is null");
    return this._program;
  }

  get programWorkouts() {
    return this.program.programWorkouts;
  }

  get phases() {
    return this.program.phases;
  }

  get sourceName(): string {
    return this._getSourceName();
  }

  get canManagePhases() {
    return (
      this._isCoach &&
      (this._viewContext === "library" ||
        this._viewContext === "athleteLibrary")
    );
  }

  get canAssignToCalendar() {
    return (
      this._isCoach &&
      this._viewContext === "athleteLibrary" &&
      this.program.startDate === null
    );
  }

  getPhaseForWeek(weekIndex: number) {
    if (this.isDated) {
      return undefined;
    }

    return this.phases.find((phase) => {
      return phase.startWeek <= weekIndex && phase.endWeek > weekIndex;
    });
  }

  get programWorkoutByWorkoutId() {
    return mapBy(this.programWorkouts, (x) => {
      return x.workout.id;
    });
  }

  get programWorkoutsById() {
    return mapBy(this.programWorkouts, (x) => {
      return x.id;
    });
  }

  get workouts() {
    return this.programWorkouts.map((x) => {
      return x.workout;
    });
  }

  get workoutsById() {
    return mapBy(this.workouts, (x) => {
      return x.id;
    });
  }

  get workoutItemsById() {
    return this.workoutGrid.workoutItemsById;
  }

  get workoutItems() {
    return Object.values(this.workoutItemsById);
  }

  /**
   * Grouped workouts - delegated to strategy.
   * Sequential mode: groups by day index (number)
   * Dated mode: groups by date string (yyyy-MM-dd)
   */
  get workoutsGrouped(): GroupedWorkouts {
    return this._gridStrategy.workoutsGrouped;
  }

  /**
   * Get workouts for a specific grouping key.
   * In sequential mode: pass dayIndex
   * In dated mode: pass date string (yyyy-MM-dd)
   */
  getWorkoutsForKey(
    key: number | string,
  ): Api.GetProgram.Output.ProgramWorkout[] {
    return this._gridStrategy.getWorkoutsForKey(key);
  }

  /**
   * Get workouts for a cell by cellId.
   * Converts cellId to the appropriate key based on current mode.
   */
  getProgramWorkouts(cellId: number): Api.GetProgram.Output.ProgramWorkout[] {
    const weekIndex = Math.floor(cellId / 7);
    const dayOfWeek = cellId % 7;
    return this.getWorkoutsForCell(weekIndex, dayOfWeek);
  }

  // ============================================================================
  // Grid Strategy Delegation
  // ============================================================================

  getWorkoutsForCell(
    weekIndex: number,
    dayOfWeek: number,
  ): Api.GetProgram.Output.ProgramWorkout[] {
    return this._gridStrategy.getWorkoutsForCell(weekIndex, dayOfWeek);
  }

  /**
   * Get ISO date string for a cell (yyyy-MM-dd).
   * Only valid in dated mode - throws assertion error in sequential mode.
   */
  getCellDate(cellId: number): string {
    return this._gridStrategy.getCellDate(cellId);
  }

  getHeaderLabels(): Array<{ primary: string; secondary?: string }> {
    return this._gridStrategy.getHeaderLabels();
  }

  getCellDateLabel(weekIndex: number, dayOfWeek: number): string | null {
    return this._gridStrategy.getCellDateLabel(weekIndex, dayOfWeek);
  }

  isCellToday(weekIndex: number, dayOfWeek: number): boolean {
    return this._gridStrategy.isCellToday(weekIndex, dayOfWeek);
  }

  // ============================================================================

  getProgramWorkout(programWorkoutId: string) {
    const item = this.programWorkoutsById[programWorkoutId];
    assert(item);
    return item;
  }

  getProgramWorkoutActions(programWorkoutId: string): WorkoutCardAction[] {
    const programWorkout = this.getProgramWorkout(programWorkoutId);
    const actions: WorkoutCardAction[] = [
      workoutCardActions.selectWorkout,
      workoutCardActions.copyWorkout,
    ];

    if (!programWorkout.workout.isInLibrary) {
      actions.push(workoutCardActions.exportToLibrary);
    }

    if (this._viewContext === "library") {
      actions.push(workoutCardActions.deleteWorkout);
      return actions;
    }

    if (this._viewContext === "athleteLibrary") {
      if (!programWorkout.workout.isCompleted) {
        actions.push(workoutCardActions.deleteWorkout);
      }

      return actions;
    }

    if (this._viewContext === "workflow") {
      actions.push(workoutCardActions.deleteWorkout);
      return actions;
    }

    return [];
  }

  getAddButtonActions(): AddButtonAction[] {
    if (!this.canEditWorkouts) {
      return [];
    }

    const actions: AddButtonAction[] = [
      addButtonActions.addWorkout,
      addButtonActions.assignProgram,
    ];

    return actions;
  }

  getWeekNumber(weekIndex: number): number | null {
    return this.isDated ? null : weekIndex + 1;
  }

  get numberOfWeeksByLastWorkoutOrder() {
    const programWorkouts = sort(this.program.programWorkouts).asc("day");

    const lastProgramWorkout = programWorkouts[programWorkouts.length - 1];

    if (!lastProgramWorkout) {
      return 1;
    }

    return Math.ceil((lastProgramWorkout.day + 1) / 7);
  }

  get numberOfWeeksByLastPhase() {
    return this.phases.reduce((maximum, phase) => {
      return Math.max(maximum, phase.endWeek);
    }, 0);
  }

  get rowCountWithoutPhases() {
    const baseRowCount = Math.max(1, this.numberOfWeeksByLastWorkoutOrder);

    return Math.max(this._rowCount ?? baseRowCount, baseRowCount);
  }

  get rowCount() {
    return Math.max(this.rowCountWithoutPhases, this.numberOfWeeksByLastPhase);
  }

  addRow() {
    this._rowCount = this.rowCount + 1;
  }

  updateProgram(data: Partial<Api.GetProgram.Output.Self>) {
    Object.assign(this._program, data);
  }

  replacePhases(
    phases: ProgramPhase[],
    phaseStructureKey: string | null = null,
  ) {
    this.program.phases = phases;
    this.program.phaseStructureKey = phaseStructureKey;
  }

  removeWeekFromPhases(weekIndex: number) {
    this.program.phases = this.phases.flatMap((phase) => {
      if (phase.endWeek <= weekIndex) return [phase];

      if (phase.startWeek > weekIndex) {
        return [
          {
            ...phase,
            startWeek: phase.startWeek - 1,
            endWeek: phase.endWeek - 1,
          },
        ];
      }

      if (phase.endWeek === phase.startWeek + 1) return [];

      return [{ ...phase, endWeek: phase.endWeek - 1 }];
    });
  }

  updateProgramWorkout(
    id: string,
    data: Partial<Api.GetProgram.Output.ProgramWorkout>,
  ) {
    const programWorkout = this.programWorkoutsById[id];
    Object.assign(programWorkout, data);
  }

  addProgramWorkouts(programWorkouts: Api.GetProgram.Output.ProgramWorkout[]) {
    programWorkouts.forEach((programWorkout) => {
      this.addProgramWorkout(programWorkout);
    });
  }

  removeProgramWorkouts(programWorkoutIds: string[]) {
    const s = new Set(programWorkoutIds);
    this.program.programWorkouts = this.program.programWorkouts.filter((pw) => {
      return !s.has(pw.id);
    });
  }

  addProgramWorkout(programWorkout: Api.GetProgram.Output.ProgramWorkout) {
    const group = this._gridStrategy.getGroup(programWorkout);
    orderUtilsMutable.insertAtTheBottom(
      this.program.programWorkouts,
      programWorkout,
      group,
    );
  }

  applyProgramBulkOperations(result: ProgramBulkOperationResult): void {
    const deletedProgramWorkoutIds: string[] = [];

    result.operations.forEach((operation) => {
      if (operation.operation === "create") {
        this.program.programWorkouts.push(
          operation.data as Api.GetProgram.Output.ProgramWorkout,
        );
        return;
      }

      if (operation.operation === "update") {
        const { id, ...data } = operation.data;
        this.updateProgramWorkout(id, data);
        return;
      }

      deletedProgramWorkoutIds.push(operation.data);
    });

    this.removeProgramWorkouts(deletedProgramWorkoutIds);
  }

  selectCell(cellId: number): void {
    this._selectedCellIds.set(cellId, true);
  }

  deselectCell(cellId: number): void {
    this._selectedCellIds.delete(cellId);
  }

  isCellSelected(cellId: number): boolean {
    return this._selectedCellIds.has(cellId);
  }

  moveProgramWorkout(
    programWorkoutId: string,
    destinationCellId: number,
    destinationOrder: number,
  ): { destinationDay: number; destinationDate: string | null } {
    const item = this.programWorkoutsById[programWorkoutId];

    if (!item) {
      throw new Error("Program workout not found");
    }

    const sourceGroup = this._gridStrategy.getGroup(item);
    const destGroup = this._gridStrategy.getGroupByCellId(destinationCellId);
    const groupKey = this._gridStrategy.getGroupKey();
    const groupValue =
      this._gridStrategy.getGroupValueForCellId(destinationCellId);

    if (groupKey === "date") {
      orderUtilsMutable.move(
        item,
        sourceGroup,
        destGroup,
        destinationOrder,
        groupValue as string,
        "date",
      );
    } else {
      orderUtilsMutable.move(
        item,
        sourceGroup,
        destGroup,
        destinationOrder,
        groupValue as number,
        "day",
      );
    }

    return {
      destinationDay: this.isDated ? -1 : destinationCellId,
      destinationDate: this.isDated
        ? this.getCellDate(destinationCellId)
        : null,
    };
  }
}
